import { Component, Vue } from 'vue-property-decorator';
import { PAGE_NAMES, PROJECT_PEER_EVALUATION_STATUSES } from '@/constants';
import {
  DisplayStatus,
  MemberPeerEvaluationDTO,
  ProjectAssessmentDTO,
  ProjectDTO,
  ProjectPeerEvaluationDetailDTO,
  TableHeaderColumns,
  UserDTO,
  UserEngagementDTO
} from '@/models';
import {
  fetchUserEvaluationDetail,
  getAveragePeerRatingPoint,
  sortArrByNumberField,
  sortArrByUserDTOField
} from '@/services';
import Avatar from '@/components/common/avatar/Avatar.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import ProjectEvaluationHeader from '@/components/common/project-evaluation-header/ProjectEvaluationHeader.vue';
import Status from '@/components/common/status/Status.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import DropsBreakDown from '@/components/pages/view-details-peer-rating/drops-break-down/DropsBreakDown.vue';
import PopupPeerRating from '@/components/pages/project-evaluation/project-peer-evaluation/pop-up-peer-rating/PopupPeerRating.vue';
import ViewEditHistory from '@/components/pages/view-details-peer-rating/view-edit-history/ViewEditHistory.vue';

@Component({
  components: {
    Avatar,
    DropsBreakDown,
    InlineLoader,
    Pagination,
    PopupPeerRating,
    ProjectEvaluationHeader,
    Status,
    UserInformation,
    ViewEditHistory
  }
})
export default class ViewDetailsPeerRating extends Vue {
  $refs: {
    popupEditRef: HTMLFormElement;
  };

  private projectInfo: ProjectDTO = {} as ProjectDTO;
  private projectFetching = true;
  private evaluationFetching = true;
  private projectPeerEvaluationDetail: ProjectPeerEvaluationDetailDTO = {} as ProjectPeerEvaluationDetailDTO;
  private membersRated: MemberPeerEvaluationDTO[] = [];
  private userEngagement: UserEngagementDTO = {} as UserEngagementDTO;
  private editHistoryDialogShown = false;
  private tableHeaderNames = {
    ACCOUNTABILITY: 'Accountability',
    COMMUNICATION: 'Communication',
    SUPPORTIVENESS: 'Supportiveness',
    PERSONAL_ATTRIBUTES: 'Personal Attributes',
    PROFESSIONAL_CONDUCT: 'Professional Conduct',
    AVERAGE_RATING: 'Average Rating',
    RECEIVED_DROPS: 'Received Drops'
  };
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  isAssessmentBelongsToProject(): boolean {
    const { projectAssessmentId } = this.$route.params;
    return this.projectInfo.projectAssessments.some(item => item.id === parseInt(projectAssessmentId));
  }

  get currentUser(): UserDTO {
    return this.$store.getters['app/currentUser'];
  }

  get tableHeaderColumns(): TableHeaderColumns {
    return [
      {
        text: 'Member',
        value: 'user'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.ACCOUNTABILITY,
        value: 'accountabilityPoint',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.COMMUNICATION,
        value: 'communicationPoint',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.SUPPORTIVENESS,
        value: 'supportivenessPoint',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.PERSONAL_ATTRIBUTES,
        value: 'personalAttributesPoint',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.PROFESSIONAL_CONDUCT,
        value: 'professionalConductPoint',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.AVERAGE_RATING,
        value: 'averageRating',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.RECEIVED_DROPS,
        value: 'givingDrops',
        class: 'grey--text'
      },
      {
        align: 'end',
        value: 'actions',
        sortable: false
      }
    ];
  }

  get displayStatus(): DisplayStatus {
    const { peerEvaluationStatus } = this.userEngagement;
    return PROJECT_PEER_EVALUATION_STATUSES[peerEvaluationStatus];
  }

  get isPeerRatingDone(): boolean {
    const { peerEvaluationStatus, user } = this.userEngagement;
    return (
      peerEvaluationStatus === PROJECT_PEER_EVALUATION_STATUSES.DONE.label &&
      this.membersRated.length >= 0 &&
      this.membersRated.filter(mr => mr.recipient.id === user.id).length === this.membersRated.length
    );
  }

  get peerEvaluationDetails(): MemberPeerEvaluationDTO[] {
    const { membersRated, userEngagement } = this;
    return this.userEngagements
      .filter(ue => ue.user.id !== userEngagement.user.id)
      .map(ue => {
        const memberRated = membersRated.find(mr => mr.user.id === ue.user.id);
        if (memberRated) {
          return memberRated;
        }
        return {
          user: ue.user,
          recipient: userEngagement.user,
          projectAssessmentId: 123,
          accountabilityPoint: 0,
          professionalConductPoint: 0,
          communicationPoint: 0,
          personalAttributesPoint: 0,
          supportivenessPoint: 0,
          givingDrops: 0,
          keepDoing: '',
          stopDoing: ''
        } as MemberPeerEvaluationDTO;
      });
  }

  customSort(items: MemberPeerEvaluationDTO[], fields: string[], isDescArr: boolean[]): MemberPeerEvaluationDTO[] {
    const field = fields[0];
    if (field) {
      const isDesc = isDescArr[0];
      switch (field) {
        case 'user': {
          return sortArrByUserDTOField(items, 'user', isDesc);
        }
        case 'accountabilityPoint':
        case 'communicationPoint':
        case 'supportivenessPoint':
        case 'personalAttributesPoint':
        case 'professionalConductPoint':
        case 'givingDrops': {
          return sortArrByNumberField(items, field, isDesc);
        }
        case 'averageRating': {
          const multiplier = isDesc ? -1 : 1;
          return items.sort((ue1, ue2) => multiplier * (this.peerAverageRating(ue1) - this.peerAverageRating(ue2)));
        }
        default: {
          return items;
        }
      }
    }
    return items;
  }

  formatDisplayPoint(point: number): string {
    if (point) {
      return point.toString();
    } else {
      return 'pending...';
    }
  }

  get userEngagements(): UserEngagementDTO[] {
    return this.$store.getters['project/userEngagements'];
  }

  get isSelfProject(): boolean {
    return this.userEngagements.length === 1;
  }

  peerAverageRating(userEngagement: MemberPeerEvaluationDTO): number {
    return Number.parseFloat(getAveragePeerRatingPoint(userEngagement).toFixed(1)) || 0;
  }

  openViewComment(peerEvaluation: MemberPeerEvaluationDTO): void {
    this.$refs.popupEditRef.openMenu(peerEvaluation, true);
  }

  receivedDrops(userEngagement: MemberPeerEvaluationDTO): number {
    return (
      userEngagement.accountabilityPoint +
      userEngagement.communicationPoint +
      userEngagement.personalAttributesPoint +
      userEngagement.professionalConductPoint +
      userEngagement.supportivenessPoint
    );
  }

  getProjectInformation(): void {
    const { projectAssessmentId } = this.$route.params;
    const projectInfo = this.$store.getters['project/project'];
    const projectAssessment = projectInfo.projectAssessments.find(
      (pa: ProjectAssessmentDTO) => pa.id === parseInt(projectAssessmentId)
    );
    this.$store.commit('project/setSelectedProjectAssessmentId', projectAssessment.assessmentPeriodId);
    this.projectInfo = projectInfo;
    if (!this.isAssessmentBelongsToProject()) {
      this.$router.push({ name: PAGE_NAMES.HOME });
      return;
    }
    this.projectFetching = false;
  }

  async fetchEvaluationDetail(): Promise<void> {
    const { projectAssessmentId, userId } = this.$route.params;
    const { data } = await fetchUserEvaluationDetail(parseInt(projectAssessmentId), parseInt(userId));
    if (data) {
      this.membersRated = data.peerEvaluations;
      this.userEngagement = data.userEngagement;
      this.projectPeerEvaluationDetail = data;
      this.evaluationFetching = false;
    } else {
      this.$router.push({ name: PAGE_NAMES.NOT_FOUND });
    }
  }

  async created(): Promise<void> {
    this.getProjectInformation();
    await this.fetchEvaluationDetail();
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }
}
